import {Box} from '@material-ui/core';
import {MOON_500} from '@wandb/weave/common/css/color.styles';
import {TargetBlank} from '@wandb/weave/common/util/links';
import {Alert} from '@wandb/weave/components/Alert';
import {Button} from '@wandb/weave/components/Button';
import {Icon} from '@wandb/weave/components/Icon';
import {LinearProgress} from '@wandb/weave/components/LinearProgress';
import {WaveLoader} from '@wandb/weave/components/Loaders/WaveLoader';
import {Tag} from '@wandb/weave/components/Tag';
import {parseWeaveRef} from '@wandb/weave/react';
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';

import {useWeaveflowCurrentRouteContext} from '../../context';
import {Empty} from '../common/Empty';
import {CompareEvaluationsPageContent} from '../CompareEvaluationsPage/CompareEvaluationsPage';
import {useGetTraceServerClientContext} from '../wfReactInterface/traceServerClientContext';
import {sanitizeObjectId} from '../wfReactInterface/traceServerDirectClient';
import {useCall} from '../wfReactInterface/tsDataModelHooks';
import {LabeledTextArea, LabeledTextField} from './components';
import {BORDER_COLOR, SECONDARY_BACKGROUND_COLOR} from './constants';
import {
  EvaluationPlaygroundPageProvider,
  useEvaluationPlaygroundPageContext,
} from './context';
import {DatasetColumnsProvider} from './DatasetColumnsContext';
import {DatasetConfigSection} from './DatasetConfigSection';
import {
  DatasetEditorRef,
  ExistingDatasetEditor,
  NewDatasetEditor,
} from './DatasetEditor';
import {EvaluationPicker} from './EvaluationConfigSection';
import {Column, ConfigSection, Header, Row} from './layout';
import {Footer} from './layout';
import {
  ModelsConfigSection,
  ModelsConfigSectionRef,
} from './ModelsConfigSection';
import {
  createEvaluation,
  getAggregateEvaluationCompletionPercentages,
  getAllVersionsOfObject,
  runEvaluation,
} from './query';
import {
  ScorersConfigSection,
  ScorersConfigSectionRef,
} from './ScorersConfigSection';
import {initializeDemoConfig} from './state';

type EvaluationPlaygroundPageProps = {
  entity: string;
  project: string;
};

const POLL_MS = 2000;
const CONFIG_PANEL_WIDTH = 500;
/**
 * Main entry point for the Evaluation Playground page.
 * Wraps the inner component with necessary providers and layout.
 */
export const EvaluationPlaygroundPage = (
  props: EvaluationPlaygroundPageProps
) => {
  return (
    <EvaluationPlaygroundPageProvider>
      <DatasetColumnsProvider>
        <EvaluationPlaygroundPageInner {...props} />
      </DatasetColumnsProvider>
    </EvaluationPlaygroundPageProvider>
  );
};

// Helper functions to reduce repetition
const getValidRefs = <T extends {originalSourceRef: string | null}>(
  items: T[]
): string[] => {
  return items
    .filter(item => item.originalSourceRef !== null)
    .map(item => item.originalSourceRef!);
};

const refStringToDisplayName = (ref: string) => {
  const parsedRef = parseWeaveRef(ref);
  return parsedRef.artifactName;
};

/**
 * Hook to get the version label for a given ref (e.g., "latest", "v1", "v2", etc.)
 */
const useVersionLabel = (
  entity: string,
  project: string,
  ref: string | null
): string | undefined => {
  const [versionLabel, setVersionLabel] = useState<string | undefined>(
    undefined
  );
  const getClient = useGetTraceServerClientContext();

  const fetchVersionLabel = useCallback(
    async (refString: string) => {
      try {
        const parsedRef = parseWeaveRef(refString);
        const objectId = parsedRef.artifactName;
        const client = getClient();

        const versions = await getAllVersionsOfObject(
          client,
          entity,
          project,
          objectId
        );

        // Find the index of this ref in the versions array
        const refIndex = versions.findIndex(v => v === refString);
        if (refIndex === -1) {
          setVersionLabel('latest'); // fallback
          return;
        }

        // Calculate version label like in VersionedObjectPicker
        const label =
          refIndex === 0 ? 'latest' : `v${versions.length - refIndex}`;
        setVersionLabel(label);
      } catch (error) {
        console.error('Failed to fetch version label:', error);
        setVersionLabel('latest'); // fallback
      }
    },
    [entity, project, getClient]
  );

  useEffect(() => {
    if (ref) {
      fetchVersionLabel(ref);
    } else {
      setVersionLabel(undefined);
    }
  }, [ref, fetchVersionLabel]);

  return versionLabel;
};

/**
 * Core component that manages the evaluation configuration and execution flow.
 * Split into three main states:
 * 1. Configuration - Setting up the evaluation parameters
 * 2. Running - Executing the evaluation
 * 3. Results - Viewing the evaluation results
 */
const EvaluationPlaygroundPageInner: React.FC<
  EvaluationPlaygroundPageProps
> = ({entity, project}) => {
  const {config, editConfig} = useEvaluationPlaygroundPageContext();
  const getClient = useGetTraceServerClientContext();
  const router = useWeaveflowCurrentRouteContext();

  // Get version label for the dataset
  const sourceRef =
    config.evaluationDefinition.properties.dataset.originalSourceRef;
  const versionLabel = useVersionLabel(entity, project, sourceRef);

  const onLoadDemoEvaluation = useCallback(() => {
    editConfig(draft => {
      return initializeDemoConfig();
    });
  }, [editConfig]);

  // const onStartFromScratch = useCallback(() => {
  //   editConfig(draft => {
  //     // I previously invoked the following line,
  //     // but that clears the other settings. I think
  //     // we want this to be a no-op, but leaving
  //     // the code here until this workflow settles.
  //     // return initializeEmptyConfig();
  //   });
  // }, [editConfig]);

  // Dataset editor refs
  const datasetEditorRef = useRef<DatasetEditorRef | null>(null);

  // Dataset editor state for triggering re-renders
  const [datasetEditorState, setDatasetEditorState] = useState<{
    hasChanges: boolean;
    isSaving: boolean;
  }>({
    hasChanges: false,
    isSaving: false,
  });

  // Dataset editor state management
  const [newDatasetEditorMode, setNewDatasetEditorMode] = useState<
    'new-empty' | 'new-file' | null
  >(null);
  const [shouldLoadDemo, setShouldLoadDemo] = useState(false);

  // View mode state (controlled) - start with empty state
  type ViewMode = 'results' | 'saving' | 'running' | 'dataset' | 'empty';
  const [viewMode, setViewMode] = useState<ViewMode>('empty');

  // Evaluation execution state
  const [evaluationResults, setEvaluationResults] = useState<string[] | null>(
    null
  );
  const [isRunning, setIsRunning] = useState(false);
  const [isSavingConfig, setIsSavingConfig] = useState(false);
  const [evaluationProgress, setEvaluationProgress] = useState<number>(0);

  // Abort controller for cancelling running evaluations
  const abortControllerRef = useRef<AbortController | null>(null);
  const pollTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const wasCancelledRef = useRef<boolean>(false);

  // Results view state
  const [selectedMetrics, setSelectedMetrics] = useState<Record<
    string,
    boolean
  > | null>(null);

  // Form validation state
  const [touchedFields, setTouchedFields] = useState<{
    name: boolean;
    description: boolean;
  }>({
    name: false,
    description: false,
  });

  // Determine which dataset editor mode to use based on current state
  const datasetEditorMode = useMemo(() => {
    if (sourceRef == null) {
      return newDatasetEditorMode;
    }
    return 'existing';
  }, [newDatasetEditorMode, sourceRef]);

  const onNewDatasetSaveComplete = useCallback(
    (datasetRef?: string) => {
      editConfig(draft => {
        draft.evaluationDefinition.properties.dataset.originalSourceRef =
          datasetRef ?? null;
      });
    },
    [editConfig]
  );

  const markFieldTouched = useCallback((field: 'name' | 'description') => {
    setTouchedFields(prev => ({...prev, [field]: true}));
  }, []);

  // Function to cancel a running evaluation
  const cancelEvaluation = useCallback(() => {
    wasCancelledRef.current = true;
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    if (pollTimeoutRef.current) {
      clearTimeout(pollTimeoutRef.current);
      pollTimeoutRef.current = null;
    }
    setIsRunning(false);
    setIsSavingConfig(false);
    setViewMode('dataset');
    setEvaluationProgress(0);
  }, []);

  // Validation logic for enabling the "Run eval" button
  const [scorersHaveErrors, setScorersHaveErrors] = useState(false);
  const [modelsHaveErrors, setModelsHaveErrors] = useState(false);

  const isRunEvalEnabled = useMemo(() => {
    const {evaluationDefinition, models} = config;

    // Check required text fields
    if (!evaluationDefinition.properties.name.trim()) {
      return false;
    }

    // Check at least one scorer is configured
    if (evaluationDefinition.properties.scorers.length === 0) {
      return false;
    }

    // Check at least one model is configured
    if (models.length === 0) {
      return false;
    }

    // Gate on validation from scorers and models
    if (scorersHaveErrors || modelsHaveErrors) {
      return false;
    }

    return true;
  }, [config, scorersHaveErrors, modelsHaveErrors]);

  /**
   * Main evaluation execution handler.
   * Creates the evaluation object and runs it with the configured models.
   */
  const handleRunEval = useCallback(
    async (
      scorerRefs?: string[],
      modelRefs?: string[],
      datasetRef?: string
    ) => {
      if (!isRunEvalEnabled || isRunning) {
        return;
      }

      // Reset cancellation flag
      wasCancelledRef.current = false;

      setIsRunning(true);
      setViewMode('running');
      setEvaluationResults(null); // Clear any previous results
      setEvaluationProgress(0);

      // Create a new abort controller for this evaluation
      abortControllerRef.current = new AbortController();

      const client = getClient();
      const {evaluationDefinition, models} = config;

      // Use provided refs or extract from current configuration
      const finalScorerRefs =
        scorerRefs ?? getValidRefs(evaluationDefinition.properties.scorers);
      const finalModelRefs = modelRefs ?? getValidRefs(models);
      const finalDatasetRef =
        datasetRef ?? evaluationDefinition.properties.dataset.originalSourceRef;

      if (!finalDatasetRef) {
        throw new Error('Dataset ref is required but was not found');
      }

      try {
        // Create the evaluation object
        const evaluationRef = await createEvaluation(client, entity, project, {
          name: sanitizeObjectId(evaluationDefinition.properties.name),
          description: evaluationDefinition.properties.description,
          datasetRef: finalDatasetRef,
          scorerRefs: finalScorerRefs,
        });

        // Update the config with the created evaluation ref
        await editConfig(draft => {
          draft.evaluationDefinition.originalSourceRef = evaluationRef;
        });

        // Execute the evaluation and get call IDs
        const callIds = await runEvaluation(
          client,
          entity,
          project,
          evaluationRef,
          finalModelRefs
        );
        // Poll for completion progress
        const pollProgress = async () => {
          try {
            // Check if cancelled
            if (
              abortControllerRef.current?.signal.aborted ||
              wasCancelledRef.current
            ) {
              return;
            }

            const progress = await getAggregateEvaluationCompletionPercentages(
              client,
              entity,
              project,
              callIds
            );
            setEvaluationProgress(progress);

            // If not complete, continue polling
            if (progress < 1) {
              // Store timeout ref for cancellation
              pollTimeoutRef.current = setTimeout(pollProgress, POLL_MS); // Poll every 2 seconds
            } else {
              // Evaluation is complete, set results and clear running state
              // But only if not cancelled
              if (!wasCancelledRef.current) {
                setEvaluationResults(callIds);
                setViewMode('results');
              }
              setIsRunning(false);
              setEvaluationProgress(0);
              abortControllerRef.current = null;
              pollTimeoutRef.current = null;
            }
          } catch (error) {
            console.error('Error polling evaluation progress:', error);
            // On error, still set results to show what we have (unless cancelled)
            if (
              !abortControllerRef.current?.signal.aborted &&
              !wasCancelledRef.current
            ) {
              setEvaluationResults(callIds);
              setViewMode('results');
            }
            setIsRunning(false);
            setEvaluationProgress(0);
            abortControllerRef.current = null;
            pollTimeoutRef.current = null;
          }
        };

        // Start polling
        pollProgress();
      } catch (error) {
        console.error('Failed to run evaluation:', error);
        // TODO: Add proper error handling/display
        setIsRunning(false);
        setEvaluationProgress(0);
        abortControllerRef.current = null;
        pollTimeoutRef.current = null;
      }
    },
    [
      config,
      entity,
      project,
      isRunEvalEnabled,
      isRunning,
      getClient,
      editConfig,
    ]
  );

  const [showEvaluationPicker, setShowEvaluationPicker] = useState(false);

  // Refs for config sections to save unsaved changes
  const scorersConfigRef = useRef<ScorersConfigSectionRef | null>(null);
  const modelsConfigRef = useRef<ModelsConfigSectionRef | null>(null);

  const headerTitle = useMemo(() => {
    switch (viewMode) {
      case 'results':
        return 'Eval results';
      case 'saving':
        return 'Saving configuration...';
      case 'running':
        // Maintain existing UX that labels running as "Results"
        return 'Results';
      case 'empty':
        return 'Evaluation Playground';
      case 'dataset':
      default:
        // Use dataset name with version tag if we have a saved dataset, otherwise "New dataset"
        if (sourceRef) {
          const datasetName = refStringToDisplayName(sourceRef);
          return (
            <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
              <span>{datasetName}</span>
              {versionLabel && <Tag label={versionLabel} color="moon" />}
            </div>
          );
        }
        return 'New dataset';
    }
  }, [viewMode, sourceRef, versionLabel]);

  // Wrap handleRunEval to save all unsaved changes first
  const handleRunEvalWithSave = useCallback(async () => {
    // Start the saving process
    setIsSavingConfig(true);
    setViewMode('saving');

    try {
      // Save dataset first if there's one being edited
      let freshDatasetRef: string | undefined;
      if (datasetEditorRef.current) {
        const savedDatasetRef = await datasetEditorRef.current.save();
        if (savedDatasetRef) {
          freshDatasetRef = savedDatasetRef;
          // Update the config with the saved dataset ref
          editConfig(draft => {
            draft.evaluationDefinition.properties.dataset.originalSourceRef =
              savedDatasetRef;
          });
        }
      }

      // Save all unsaved changes and get fresh refs
      const promises: {
        scorerRefs?: Promise<string[]>;
        modelRefs?: Promise<string[]>;
      } = {};

      if (scorersConfigRef.current?.saveAllUnsavedAndGetRefs) {
        promises.scorerRefs =
          scorersConfigRef.current.saveAllUnsavedAndGetRefs();
      }

      if (modelsConfigRef.current?.saveAllUnsavedAndGetRefs) {
        promises.modelRefs = modelsConfigRef.current.saveAllUnsavedAndGetRefs();
      }

      // Wait for all saves to complete and get the fresh refs
      const [freshScorerRefs, freshModelRefs] = await Promise.all([
        promises.scorerRefs || Promise.resolve(undefined),
        promises.modelRefs || Promise.resolve(undefined),
      ]);

      // Transition from saving to running
      setIsSavingConfig(false);

      // Run the evaluation with the fresh refs
      await handleRunEval(freshScorerRefs, freshModelRefs, freshDatasetRef);
    } catch (error) {
      console.error('Failed to save configuration:', error);
      setIsSavingConfig(false);
      setViewMode('dataset');
    }
  }, [
    handleRunEval,
    datasetEditorRef,
    editConfig,
    scorersConfigRef,
    modelsConfigRef,
  ]);

  // Show config panel for dataset & saving modes (hide during running & results)
  const showConfigPanel = viewMode === 'dataset' || viewMode === 'saving';

  const renderHeaderAction = () => {
    if (viewMode === 'running' || viewMode === 'saving') {
      return (
        <Button
          icon="close"
          size="small"
          variant="secondary"
          onClick={cancelEvaluation}>
          Cancel
        </Button>
      );
    }
    if (viewMode === 'dataset') {
      return (
        <Button
          variant="primary"
          onClick={() => datasetEditorRef.current?.save()}
          disabled={
            !datasetEditorState.hasChanges || datasetEditorState.isSaving
          }
          tooltip={
            !datasetEditorState.hasChanges
              ? 'No changes to save'
              : datasetEditorState.isSaving
                ? 'Saving...'
                : 'Save'
          }>
          {datasetEditorState.isSaving ? 'Saving...' : 'Save'}
        </Button>
      );
    }
    if (viewMode === 'results') {
      return evaluationResults ? (
        <TargetBlank
          href={router.compareEvaluationsUri(
            entity,
            project,
            evaluationResults,
            null
          )}>
          <Button endIcon="open-new-tab" variant="ghost">
            Open eval
          </Button>
        </TargetBlank>
      ) : null;
    }
    return null;
  };

  const renderBody = () => {
    switch (viewMode) {
      case 'empty':
        return (
          <Column
            style={{
              flex: 1,
              alignItems: 'center',
              justifyContent: 'center',
              padding: '48px',
            }}>
            <Empty
              icon="type-boolean"
              heading="Welcome to the evaluation playground"
              description={
                <>
                  Evaluate prompts against datasets and score performance using
                  an
                  <br />
                  LLM as a judge
                </>
              }
              moreInformation={
                <>
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      marginTop: '8px',
                      gap: '16px',
                      alignItems: 'center',
                    }}>
                    <div
                      style={{
                        display: 'flex',
                        gap: '12px',
                        flexWrap: 'wrap',
                        justifyContent: 'center',
                      }}>
                      <Button
                        variant="primary"
                        icon="play"
                        onClick={() => {
                          onLoadDemoEvaluation();
                          setShouldLoadDemo(true);
                          setNewDatasetEditorMode('new-empty');
                          setViewMode('dataset');
                        }}
                        data-testid="load-demo-evaluation-button">
                        Load demo example
                      </Button>
                      <Button
                        variant="secondary"
                        icon="add-new"
                        onClick={() => {
                          setShouldLoadDemo(false);
                          setNewDatasetEditorMode('new-empty');
                          setViewMode('dataset');
                        }}
                        data-testid="start-manual-evaluation-button">
                        Start from scratch
                      </Button>
                    </div>
                  </div>
                </>
              }
            />
          </Column>
        );
      case 'results':
        return (
          evaluationResults && (
            <CompareEvaluationsPageContent
              entity={entity}
              project={project}
              evaluationCallIds={evaluationResults}
              onEvaluationCallIdsUpdate={newIds => {
                // Pass - remove?
              }}
              selectedMetrics={selectedMetrics}
              setSelectedMetrics={setSelectedMetrics}
              initialTabValue="results"
              hideEvalPicker
            />
          )
        );
      case 'saving':
        return (
          <Column
            style={{
              flex: 1,
              alignItems: 'center',
              justifyContent: 'center',
              padding: '48px',
            }}>
            <WaveLoader size="small" />
            <p style={{marginTop: '16px', color: MOON_500}}>Saving...</p>
          </Column>
        );
      case 'running':
        return (
          <Column
            style={{
              flex: 1,
              alignItems: 'center',
              justifyContent: 'center',
              padding: '48px',
            }}>
            <WaveLoader size="small" />
            <p style={{marginTop: '16px', color: MOON_500}}>
              {evaluationProgress === 0
                ? 'Evaluation queued'
                : 'Running evaluation'}
            </p>

            <Box style={{width: '300px'}}>
              <LinearProgress
                variant={
                  evaluationProgress === 0 ? 'indeterminate' : 'determinate'
                }
                value={evaluationProgress * 100}
                style={{marginBottom: '8px'}}
              />
              <p
                style={{
                  fontSize: '14px',
                  color: MOON_500,
                  textAlign: 'center',
                }}>
                {evaluationProgress === 0
                  ? 'Waiting for servers to start job'
                  : `${Math.round(evaluationProgress * 100)}% complete`}
              </p>
            </Box>
          </Column>
        );
      case 'dataset':
      default:
        return (
          <div
            style={{
              flex: 1,
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }}>
            {datasetEditorMode === 'new-empty' && (
              <NewDatasetEditor
                ref={datasetEditorRef}
                entity={entity}
                project={project}
                onSaveComplete={onNewDatasetSaveComplete}
                showNoRowsOverlay={false}
                shouldLoadDemo={shouldLoadDemo}
                evaluationTitle={config.evaluationDefinition.properties.name}
                onChange={setDatasetEditorState}
              />
            )}
            {datasetEditorMode === 'new-file' && (
              <NewDatasetEditor
                ref={datasetEditorRef}
                entity={entity}
                project={project}
                useFilePicker
                onSaveComplete={onNewDatasetSaveComplete}
                evaluationTitle={config.evaluationDefinition.properties.name}
                onChange={setDatasetEditorState}
              />
            )}
            {datasetEditorMode === 'existing' && sourceRef && (
              <ExistingDatasetEditor
                ref={datasetEditorRef}
                datasetRef={sourceRef}
                onSaveComplete={onNewDatasetSaveComplete}
                onChange={setDatasetEditorState}
              />
            )}
          </div>
        );
    }
  };

  return (
    <Row style={{height: '100vh', overflow: 'hidden'}}>
      <div
        style={{
          width: showConfigPanel ? CONFIG_PANEL_WIDTH : '0px',
          transition: 'width 300ms ease-in-out',
          flex: '0 0 auto',
          overflow: 'hidden',
          height: '100%',
        }}>
        <div
          style={{
            width: CONFIG_PANEL_WIDTH,
            height: '100%',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          }}>
          <ConfigPanel
            entity={entity}
            project={project}
            setNewDatasetEditorMode={setNewDatasetEditorMode}
            isRunning={isRunning}
            isSavingConfig={isSavingConfig}
            touchedFields={touchedFields}
            markFieldTouched={markFieldTouched}
            isRunEvalEnabled={isRunEvalEnabled}
            handleRunEvalWithSave={handleRunEvalWithSave}
            showEvaluationPicker={showEvaluationPicker}
            setShowEvaluationPicker={setShowEvaluationPicker}
            scorersConfigRef={scorersConfigRef}
            modelsConfigRef={modelsConfigRef}
            setScorersHaveErrors={setScorersHaveErrors}
            setModelsHaveErrors={setModelsHaveErrors}
            evaluationResults={evaluationResults}
          />
        </div>
      </div>
      <Column style={{flex: '1 1 600px', overflow: 'hidden', minHeight: 0}}>
        {viewMode !== 'empty' && (
          <Header>
            <div style={{display: 'flex', alignItems: 'center', gap: '4px'}}>
              {viewMode === 'results' && (
                <Button
                  icon="chevron-back"
                  variant="ghost"
                  onClick={() => setViewMode('dataset')}></Button>
              )}
              <span>{headerTitle}</span>
            </div>
            {renderHeaderAction()}
          </Header>
        )}
        <div
          style={{
            flex: 1,
            minHeight: 0,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          }}>
          {renderBody()}
        </div>
      </Column>
    </Row>
  );
};

/**
 * Configuration panel component that contains all the evaluation setup forms.
 * Manages the evaluation, dataset, scorers, and models configuration.
 */
const ConfigPanel: React.FC<{
  entity: string;
  project: string;
  setNewDatasetEditorMode: (mode: 'new-empty' | 'new-file' | null) => void;
  isRunning: boolean;
  isSavingConfig: boolean;
  touchedFields: {name: boolean; description: boolean};
  markFieldTouched: (field: 'name' | 'description') => void;
  isRunEvalEnabled: boolean;
  handleRunEvalWithSave: () => Promise<void>;
  showEvaluationPicker: boolean;
  setShowEvaluationPicker: (show: boolean) => void;
  scorersConfigRef: React.MutableRefObject<ScorersConfigSectionRef | null>;
  modelsConfigRef: React.MutableRefObject<ModelsConfigSectionRef | null>;
  setScorersHaveErrors: (hasErrors: boolean) => void;
  setModelsHaveErrors: (hasErrors: boolean) => void;
  evaluationResults: string[] | null;
}> = ({
  entity,
  project,
  setNewDatasetEditorMode,
  isRunning,
  isSavingConfig,
  touchedFields,
  markFieldTouched,
  isRunEvalEnabled,
  handleRunEvalWithSave,
  showEvaluationPicker,
  setShowEvaluationPicker,
  scorersConfigRef,
  modelsConfigRef,
  setScorersHaveErrors,
  setModelsHaveErrors,
  evaluationResults,
}) => {
  const {config, editConfig} = useEvaluationPlaygroundPageContext();

  // Get the latest evaluation call to display its name
  const latestEvaluationCall = useCall({
    key:
      evaluationResults && evaluationResults.length > 0
        ? {
            entity,
            project,
            callId: evaluationResults[evaluationResults.length - 1],
          }
        : null,
  });
  const router = useWeaveflowCurrentRouteContext();

  return (
    <Column
      style={{
        flex: '1 1 auto',
        borderRight: `1px solid ${BORDER_COLOR}`,
        backgroundColor: SECONDARY_BACKGROUND_COLOR,
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
      }}>
      {/* Disabling the header until we can have a better experience to start from an existing evaluation */}
      {/* <Header>
        <span>Configuration</span>
        <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
          {config.evaluationDefinition.originalSourceRef && (
            <span style={{fontSize: '14px', color: '#666'}}>
              ({refStringToName(config.evaluationDefinition.originalSourceRef)})
            </span>
          )}
          <Button
            icon={showEvaluationPicker ? 'chevron-up' : 'chevron-down'}
            variant="ghost"
            tooltip={
              showEvaluationPicker
                ? 'Hide evaluation picker'
                : 'Load from a previous evaluation'
            }
            onClick={() => {
              setShowEvaluationPicker(!showEvaluationPicker);
            }}></Button>
        </div>
      </Header> */}
      <Column
        style={{
          flex: '1 1 0',
          overflowY: 'auto',
          backgroundColor: SECONDARY_BACKGROUND_COLOR,
          minHeight: 0,
        }}>
        {showEvaluationPicker && (
          <ConfigSection
            title="Load existing evaluation"
            icon="baseline-alt"
            style={{
              borderBottom: `1px solid ${BORDER_COLOR}`,
              paddingBottom: '16px',
            }}>
            <EvaluationPicker entity={entity} project={project} />
          </ConfigSection>
        )}

        {evaluationResults && (
          <div style={{padding: '16px 16px 0 16px'}}>
            <TargetBlank
              href={router.compareEvaluationsUri(
                entity,
                project,
                evaluationResults,
                null
              )}>
              <Alert icon="boolean" style={{cursor: 'pointer'}}>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    width: '100%',
                    gap: '4px',
                    fontSize: '14px',
                  }}>
                  <div
                    style={{display: 'flex', alignItems: 'center', gap: '4px'}}>
                    <span>Last evaluation run:</span>
                    <span style={{fontWeight: 600}}>
                      {latestEvaluationCall.loading
                        ? 'Loading...'
                        : latestEvaluationCall.result?.displayName ||
                          latestEvaluationCall.result?.callId}
                    </span>
                  </div>
                  <Icon name="open-new-tab" width={16} height={16} />
                </div>
              </Alert>
            </TargetBlank>
          </div>
        )}

        <div
          style={{
            padding: '16px',
            display: 'flex',
            flexDirection: 'column',
            borderBottom: `1px solid ${BORDER_COLOR}`,
            gap: '16px',
          }}>
          <LabeledTextField
            label="Title"
            value={config.evaluationDefinition.properties.name}
            onChange={value => {
              editConfig(draft => {
                draft.evaluationDefinition.properties.name = value;
              });
            }}
            placeholder="Evaluation name"
            required
            onBlur={() => markFieldTouched('name')}
          />

          <LabeledTextArea
            label="Description"
            value={config.evaluationDefinition.properties.description}
            onChange={e => {
              editConfig(draft => {
                draft.evaluationDefinition.properties.description =
                  e.target.value;
              });
            }}
            placeholder="This evaluation determines ..."
            rows={3}
            onBlur={() => markFieldTouched('description')}
          />
        </div>

        <DatasetConfigSection
          entity={entity}
          project={project}
          setNewDatasetEditorMode={setNewDatasetEditorMode}
        />

        <ModelsConfigSection
          entity={entity}
          project={project}
          ref={modelsConfigRef}
          onValidationChanged={setModelsHaveErrors}
        />

        <ScorersConfigSection
          entity={entity}
          project={project}
          ref={scorersConfigRef}
          onValidationChanged={setScorersHaveErrors}
        />
      </Column>
      <Footer>
        <div
          style={{
            display: 'flex',
            gap: '8px',
            width: '100%',
            justifyContent: 'flex-end',
          }}>
          <Button
            icon={isSavingConfig ? 'save' : 'play'}
            variant="primary"
            disabled={!isRunEvalEnabled || isRunning || isSavingConfig}
            onClick={handleRunEvalWithSave}
            tooltip={
              isRunEvalEnabled && !isRunning && !isSavingConfig
                ? 'Saves all unsaved changes before running'
                : undefined
            }>
            {isSavingConfig
              ? 'Saving...'
              : isRunning
                ? 'Running...'
                : 'Run eval'}
          </Button>
        </div>
      </Footer>
    </Column>
  );
};
